import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './searchPage.module.css';
import { API_URL } from '../Utils/api';
import {
  FaSearch, FaTimes, FaUserPlus, FaUserCheck,
  FaRegCommentDots, FaHeart, FaRegHeart,
  FaMapMarkerAlt, FaUsers
} from 'react-icons/fa';
import { useSearch } from '../hooks/useUsers';

/* ─── Helpers ──────────────────────────────────────────────── */
const timeAgo = d => {
  if (!d) return '';
  const diff = Date.now() - new Date(d);
  const m = Math.floor(diff/60000), h = Math.floor(diff/3600000), dd = Math.floor(diff/86400000);
  if (m < 1)  return 'maintenant';
  if (m < 60) return `${m}min`;
  if (h < 24) return `${h}h`;
  if (dd < 7) return `${dd}j`;
  return new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'short' });
};

const fmt = n => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n ?? 0);

const highlight = (text, query) => {
  if (!query || !text) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className={styles.mark}>{part}</mark>
      : part
  );
};

/* ─── UserCard ─────────────────────────────────────────────── */
const UserCard = ({ user, query, myUserId, token, onNavigate }) => {
  const [following, setFollowing] = useState(
    user.followers?.map(String).includes(String(myUserId)) || false
  );
  const isOwn = String(user._id) === String(myUserId);

  const handleFollow = async e => {
    e.stopPropagation();
    setFollowing(v => !v);
    try {
      await axios.put(`${API_URL}/follow`, { authorId: user._id },
        { headers: { Authorization: `Bearer${token}` } });
    } catch { setFollowing(v => !v); }
  };

  return (
    <div className={styles.userCard} onClick={() => onNavigate(`/profile/${user._id}`)}>
      <div className={styles.userAvatarWrap}>
        {user.userPP
          ? 
          <img
            src={user.userPP || 'https://via.placeholder.com/36'}
            alt={user.username}
            className={styles.userAvatar}
            onClick={e => { e.stopPropagation(); onNavigate(`/profile/${user.userId}`); }}
          />
          : <div className={styles.userAvatarPlaceholder}>{user.username?.charAt(0).toUpperCase()}</div>
        }
        {user.socketId && <span className={styles.onlineDot}/>}
      </div>

      <div className={styles.userInfo}>
        <p className={styles.userName}>{highlight(user.username, query)}</p>
        <div className={styles.userMeta}>
          <span className={styles.userMetaItem}>
            <FaUsers/> {fmt(user.followers?.length || 0)} abonnés
          </span>
        </div>
      </div>

      {!isOwn && (
        <button
          className={`${styles.followBtn} ${following ? styles.followBtnActive : ''}`}
          onClick={handleFollow}
        >
          {following ? <><FaUserCheck/> Suivi</> : <><FaUserPlus/> Suivre</>}
        </button>
      )}
    </div>
  );
};

/* ─── PostCard ─────────────────────────────────────────────── */
const PostCard = ({ post, query, myUserId, token, onNavigate }) => {
  const [liked,     setLiked]     = useState(post.postLike?.map(String).includes(String(myUserId)) || false);
  const [likeCount, setLikeCount] = useState(post.postLike?.length || 0);

  const handleLike = async e => {
    e.stopPropagation();
    setLiked(v => !v);
    setLikeCount(v => liked ? v - 1 : v + 1);
    try {
      await axios.put(`${API_URL}/post/like`, { postId: post._id, authorId: post.userId },
        { headers: { Authorization: `Bearer${token}` } });
    } catch { setLiked(v => !v); setLikeCount(v => liked ? v + 1 : v - 1); }
  };

  const postText = post.postText || post.post?.question || post.post?.title || '';
  const type     = post.post?.type || post.post?.postType;

  return (
    <article className={styles.postCard} onClick={() => onNavigate(`/post/${post._id}`)}>
      <div className={styles.postCardHeader}>
        <img
          src={post.userPP || 'https://via.placeholder.com/36'}
          alt={post.username}
          className={styles.postAvatar}
          onClick={e => { e.stopPropagation(); onNavigate(`/profile/${post.userId}`); }}
        />
        <div className={styles.postAuthorInfo}>
          <span className={styles.postAuthorName}>{highlight(post.username, query)}</span>
          <span className={styles.postTime}>{timeAgo(post.createdAt)}</span>
        </div>
        {type && (
          <span className={styles.postTypeBadge} data-type={type}>{type}</span>
        )}
      </div>

      {postText && (
        <p className={styles.postText}>{highlight(postText, query)}</p>
      )}

      {post.postPicture && (
        <div className={styles.postImageWrap}>
          <img src={post.postPicture} alt="" loading="lazy"/>
        </div>
      )}
      {post.post?.imageUrl && (
        <div className={styles.postImageWrap}>
          <img src={ post.post?.imageUrl} alt="" loading="lazy"/>
        </div>
      )}

      <div className={styles.postActions}>
        <button
          className={`${styles.postActionBtn} ${liked ? styles.postLiked : ''}`}
          onClick={handleLike}
        >
          {liked ? <FaHeart/> : <FaRegHeart/>}
          {likeCount > 0 && <span>{likeCount}</span>}
        </button>
        <button className={styles.postActionBtn}
          onClick={e => { e.stopPropagation(); onNavigate(`/post/${post._id}`); }}>
          <FaRegCommentDots/>
          {post.postComment?.length > 0 && <span>{post.postComment.length}</span>}
        </button>
      </div>
    </article>
  );
};

/* ─── SearchPage ───────────────────────────────────────────── */
const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate  = useNavigate();
  const inputRef  = useRef(null);

  const initialQ = searchParams.get('q') || '';
  const [inputVal, setInputVal]   = useState(initialQ);
  const [query,    setQuery]      = useState(initialQ);
  const [tab,      setTab]        = useState('all');
  const [searched, setSearched]   = useState(!!initialQ);

  const token    = localStorage.getItem('token');
  const myUserId = localStorage.getItem('userId');

  const { data: results = { users: [], posts: [], counts: { users: 0, posts: 0 } }, isLoading: loading } = useSearch(token, query, tab);

  /* ── Submit ── */
  const handleSubmit = e => {
    e.preventDefault();
    if (!inputVal.trim()) return;
    setQuery(inputVal.trim());
    setSearchParams({ q: inputVal.trim() });
    setSearched(true);
  };

  /* ── Changement de tab ── */
  const handleTab = t => {
    setTab(t);
  };

  const total = results.counts.users + results.counts.posts;

  return (
    <div className={styles.page}>

      {/* ── Barre de recherche ── */}
      <div className={styles.searchBar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Retour">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
        </button>

        <form className={styles.searchForm} onSubmit={handleSubmit}>
          <FaSearch className={styles.searchIcon}/>
          <input
            ref={inputRef}
            type="text"
            placeholder="Rechercher des personnes, publications..."
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            className={styles.searchInput}
            autoFocus
          />
          {inputVal && (
            <button type="button" className={styles.clearBtn}
              onClick={() => { setInputVal(''); inputRef.current?.focus(); }}>
              <FaTimes/>
            </button>
          )}
        </form>
      </div>

      {/* ── Tabs ── */}
      {searched && (
        <div className={styles.tabs}>
          {[
            ['all',   'Tout',          total],
            ['users', 'Personnes',     results.counts.users],
            ['posts', 'Publications',  results.counts.posts],
          ].map(([id, label, count]) => (
            <button
              key={id}
              className={`${styles.tab} ${tab === id ? styles.tabActive : ''}`}
              onClick={() => handleTab(id)}
            >
              {label}
              {count > 0 && <span className={styles.tabCount}>{count}</span>}
            </button>
          ))}
        </div>
      )}

      {/* ── Contenu ── */}
      <div className={styles.content}>

        {/* État initial */}
        {!searched && !loading && (
          <div className={styles.emptyState}>
            <FaSearch/>
            <p>Cherche des personnes ou des publications</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className={styles.loadingState}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className={styles.skeleton}>
                <div className={styles.skAvatar}/>
                <div className={styles.skLines}>
                  <div className={styles.skLine} style={{width:'50%'}}/>
                  <div className={styles.skLine} style={{width:'30%'}}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Aucun résultat */}
        {!loading && searched && total === 0 && (
          <div className={styles.emptyState}>
            <FaSearch/>
            <h3>Aucun résultat pour « {query} »</h3>
            <p>Essaie avec d'autres mots-clés</p>
          </div>
        )}

        {/* Résultats */}
        {!loading && searched && total > 0 && (
          <>
            {/* ── Utilisateurs ── */}
            {(tab === 'all' || tab === 'users') && results.users.length > 0 && (
              <section className={styles.section}>
                {tab === 'all' && <h2 className={styles.sectionTitle}>Personnes</h2>}
                <div className={styles.usersList}>
                  {results.users.map(user => (
                    <UserCard
                      key={user._id}
                      user={user}
                      query={query}
                      myUserId={myUserId}
                      token={token}
                      onNavigate={navigate}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* ── Publications ── */}
            {(tab === 'all' || tab === 'posts') && results.posts.length > 0 && (
              <section className={styles.section}>
                {tab === 'all' && <h2 className={styles.sectionTitle}>Publications</h2>}
                <div className={styles.postsList}>
                  {results.posts.map(post => (
                    <PostCard
                      key={post._id}
                      post={post}
                      query={query}
                      myUserId={myUserId}
                      token={token}
                      onNavigate={navigate}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;